import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Users, Palette, DotsSixVertical, ImageSquare } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import ToneOfVoiceSection from './ToneOfVoiceSection';
import PersonasSection from './PersonasSection';
import MoodboardSection from './MoodboardSection';
import ProjectDesignTab from './ProjectDesignTab';
import { ProjectLogoUpload } from './DesignV3ProjectAssets';
import ClientWorkspaceControls from './ClientWorkspaceControls';
import ContextHelpButton from '../ContextHelpButton';

const TAB_ORDER_KEY = 'sketchario:dna-tab-order:v1';
const DEFAULT_SECTIONS = [
  { id: 'design', icon: Palette, labelKey: 'project.dna.tabs.design' },
  { id: 'tov', icon: BookOpen, labelKey: 'project.dna.tabs.tov' },
  { id: 'personas', icon: Users, labelKey: 'project.dna.tabs.personas' },
];

function normalizeSection(value) {
  return value === 'client' ? 'design' : value;
}

function loadOrder() {
  try {
    const saved = JSON.parse(localStorage.getItem(TAB_ORDER_KEY) || '[]');
    const validIds = new Set(DEFAULT_SECTIONS.map(item => item.id));
    const normalized = saved.filter(id => validIds.has(id));
    for (const item of DEFAULT_SECTIONS) if (!normalized.includes(item.id)) normalized.push(item.id);
    return normalized;
  } catch {
    return DEFAULT_SECTIONS.map(item => item.id);
  }
}

export default function DnaWorkspace({ api, project, initialSection }) {
  const { t } = useTranslation();
  const { user, effectivePlan } = useAuth();
  const canUseVisualDesign = user?.role === 'admin' || ['strategist', 'custom', 'admin'].includes(String(effectivePlan || user?.plan || '').toLowerCase());
  const normalizeAllowedSection = value => {
    const normalized = normalizeSection(value);
    if (!canUseVisualDesign && ['design', 'moodboard'].includes(normalized)) return 'tov';
    return normalized;
  };
  const [section, setSection] = useState(normalizeAllowedSection(initialSection) || (canUseVisualDesign ? 'design' : 'tov'));
  const [order, setOrder] = useState(loadOrder);
  const [dragging, setDragging] = useState(null);
  const [inheritRevision, setInheritRevision] = useState(0);

  useEffect(() => {
    if (!initialSection) return;
    const normalized = normalizeSection(initialSection);
    setSection(!canUseVisualDesign && ['design', 'moodboard'].includes(normalized) ? 'tov' : normalized);
  }, [initialSection, canUseVisualDesign]);

  useEffect(() => {
    if (!canUseVisualDesign && ['design', 'moodboard'].includes(section)) setSection('tov');
  }, [canUseVisualDesign, section]);

  const sections = useMemo(() => {
    const allowed = canUseVisualDesign
      ? DEFAULT_SECTIONS
      : [
          { id: 'tov', icon: BookOpen, labelKey: 'project.dna.tabs.tov' },
          { id: 'personas', icon: Users, labelKey: 'project.dna.tabs.personas' },
          { id: 'brand', icon: ImageSquare, label: 'Brand' },
        ];
    const byId = Object.fromEntries(allowed.map(item => [item.id, item]));
    const ordered = order.map(id => byId[id]).filter(Boolean);
    return [...ordered, ...allowed.filter(item => !ordered.some(current => current.id === item.id))];
  }, [order, canUseVisualDesign]);

  const moveBefore = (sourceId, targetId) => {
    if (!sourceId || sourceId === targetId) return;
    setOrder(prev => {
      const next = prev.filter(id => id !== sourceId);
      const targetIndex = next.indexOf(targetId);
      next.splice(targetIndex < 0 ? next.length : targetIndex, 0, sourceId);
      localStorage.setItem(TAB_ORDER_KEY, JSON.stringify(next));
      return next;
    });
  };

  return (
    <div className="pt-2" data-testid="dna-workspace">
      <div className="mb-2 flex items-center gap-2 border-b border-[var(--border-color)]">
        <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto pb-1">
        {sections.map(item => (
          <button
            key={item.id}
            data-testid={`dna-tab-${item.id}`}
            draggable
            onDragStart={e => { setDragging(item.id); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', item.id); }}
            onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
            onDrop={e => { e.preventDefault(); moveBefore(e.dataTransfer.getData('text/plain') || dragging, item.id); setDragging(null); }}
            onDragEnd={() => setDragging(null)}
            className={`group flex items-center gap-1.5 px-3 py-2 text-xs md:text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              section === item.id
                ? 'border-[var(--accent-purple)] text-[var(--text-primary)]'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]'
            } ${dragging === item.id ? 'opacity-50' : ''}`}
            onClick={() => setSection(item.id)}
            title="Trascina per riordinare"
          >
            <DotsSixVertical className="hidden md:block opacity-0 group-hover:opacity-50 cursor-grab" size={13} />
            <item.icon size={16} weight={section === item.id ? 'fill' : 'regular'} />
            {item.label || t(item.labelKey)}
          </button>
        ))}
        </div>
        <ContextHelpButton articleId="design-dna-overview" label="Guida Design DNA" className="mb-1" />
      </div>

      <div key={`${section}-${inheritRevision}`}>
        {section === 'tov' && <ToneOfVoiceSection api={api} project={project} />}
        {section === 'personas' && <PersonasSection api={api} project={project} />}
        {canUseVisualDesign && section === 'moodboard' && <MoodboardSection api={api} project={project} onOpenDesign={() => setSection('design')} />}
        {!canUseVisualDesign && section === 'brand' && (
          <div className="max-w-xl"><ProjectLogoUpload api={api} projectId={project.id} /></div>
        )}
        {canUseVisualDesign && section === 'design' && (
          <div className="space-y-4">
            <ClientWorkspaceControls
              api={api}
              project={project}
              onInherited={() => setInheritRevision(value => value + 1)}
            />
            <ProjectDesignTab api={api} project={project} />
          </div>
        )}
      </div>
    </div>
  );
}
